import React, { useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink } from "react-router-dom";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import {
    FiDownload,
    FiFileText,
    FiCalendar,
    FiRefreshCw,
    FiCheckCircle,
} from "react-icons/fi";

// Format number in Indian Rupees
const formatINR = (amount) => {
    const numAmount = typeof amount === "number" ? amount : parseFloat(amount) || 0;
    return `₹${Number(numAmount.toFixed(0)).toLocaleString("en-IN")}`;
};

// Format percentage
const formatPercent = (value) => `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;

// Format date
const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

export default function CapitalGains() {
    const [startDate, setStartDate] = useState("2023-01-01");
    const [endDate, setEndDate] = useState("2024-01-15");
    const [isGenerating, setIsGenerating] = useState(false);
    const [isExportReady, setIsExportReady] = useState(false);
    const [reportGenerated, setReportGenerated] = useState(true);

    // Static Data - Summary Cards
    const summaryData = {
        totalRealizedGains: 125000,
        totalUnrealizedGains: 185000,
        stcg: 45000,
        ltcg: 80000,
        totalGains: 310000,
    };

    // Static Data - Realized Gains
    const realizedGains = [
        {
            id: 1,
            fundName: "Axis Bluechip Fund - Direct Growth",
            folioNo: "FOLIO001",
            purchaseDate: "2022-06-15",
            saleDate: "2023-08-20",
            holdingPeriod: "1Y 2M",
            purchaseNAV: 45.2,
            saleNAV: 52.3,
            units: 120.45,
            purchaseAmount: 54443.4,
            saleAmount: 62995.35,
            gain: 8551.95,
            gainPercent: 15.72,
            type: "LTCG",
            taxRate: 10,
            taxAmount: 855.2,
        },
        {
            id: 2,
            fundName: "HDFC Midcap Opportunities - Direct Growth",
            folioNo: "FOLIO001",
            purchaseDate: "2023-03-10",
            saleDate: "2023-09-15",
            holdingPeriod: "6M",
            purchaseNAV: 62.5,
            saleNAV: 68.9,
            units: 80.2,
            purchaseAmount: 5012.5,
            saleAmount: 5525.78,
            gain: 513.28,
            gainPercent: 10.24,
            type: "STCG",
            taxRate: 15,
            taxAmount: 76.99,
        },
        {
            id: 3,
            fundName: "ICICI Prudential Technology Fund - Direct Growth",
            folioNo: "FOLIO002",
            purchaseDate: "2021-12-20",
            saleDate: "2023-11-10",
            holdingPeriod: "1Y 10M",
            purchaseNAV: 38.5,
            saleNAV: 45.2,
            units: 150.75,
            purchaseAmount: 5803.88,
            saleAmount: 6813.9,
            gain: 1010.02,
            gainPercent: 17.41,
            type: "LTCG",
            taxRate: 10,
            taxAmount: 101.0,
        },
        {
            id: 4,
            fundName: "SBI Small Cap Fund - Direct Growth",
            folioNo: "FOLIO002",
            purchaseDate: "2023-01-15",
            saleDate: "2023-10-25",
            holdingPeriod: "9M",
            purchaseNAV: 35.2,
            saleNAV: 38.5,
            units: 200.5,
            purchaseAmount: 7057.6,
            saleAmount: 7719.25,
            gain: 661.65,
            gainPercent: 9.38,
            type: "STCG",
            taxRate: 15,
            taxAmount: 99.25,
        },
    ];

    // Static Data - Unrealized Gains
    const unrealizedGains = [
        {
            id: 1,
            fundName: "Axis Bluechip Fund - Direct Growth",
            folioNo: "FOLIO001",
            purchaseDate: "2023-05-20",
            currentDate: "2024-01-15",
            holdingPeriod: "8M",
            purchaseNAV: 48.5,
            currentNAV: 52.3,
            units: 150.25,
            purchaseAmount: 7297.13,
            currentValue: 7868.08,
            gain: 570.95,
            gainPercent: 7.83,
            type: "STCG",
            estimatedTax: 85.64,
        },
        {
            id: 2,
            fundName: "Kotak Equity Hybrid Fund - Direct Growth",
            folioNo: "FOLIO001",
            purchaseDate: "2022-08-10",
            currentDate: "2024-01-15",
            holdingPeriod: "1Y 5M",
            purchaseNAV: 52.2,
            currentNAV: 55.8,
            units: 95.3,
            purchaseAmount: 4974.66,
            currentValue: 5317.74,
            gain: 343.08,
            gainPercent: 6.89,
            type: "LTCG",
            estimatedTax: 34.31,
        },
        {
            id: 3,
            fundName: "UTI Nifty Index Fund - Direct Growth",
            folioNo: "FOLIO002",
            purchaseDate: "2022-03-15",
            currentDate: "2024-01-15",
            holdingPeriod: "1Y 10M",
            purchaseNAV: 38.5,
            currentNAV: 42.3,
            units: 300.25,
            purchaseAmount: 11559.63,
            currentValue: 12700.58,
            gain: 1140.95,
            gainPercent: 9.87,
            type: "LTCG",
            estimatedTax: 114.1,
        },
    ];

    // Chart Data - Gains by Type
    const gainsChartData = [
        { name: "STCG", realized: 1174.93, unrealized: 570.95 },
        { name: "LTCG", realized: 9561.97, unrealized: 1484.03 },
    ];

    const handleGenerate = () => {
        setIsGenerating(true);
        // Simulate async generation for large date ranges
        setTimeout(() => {
            setIsGenerating(false);
            setReportGenerated(true);
            setIsExportReady(true);
        }, 2000);
    };

    const handleExport = () => {
        if (!isExportReady) {
            alert("Please generate the report first");
            return;
        }
        alert("Export functionality will be implemented");
    };

    // Calculate totals
    const totalRealizedSTCG = realizedGains
        .filter((g) => g.type === "STCG")
        .reduce((sum, g) => sum + g.gain, 0);
    const totalRealizedLTCG = realizedGains
        .filter((g) => g.type === "LTCG")
        .reduce((sum, g) => sum + g.gain, 0);
    const totalUnrealizedSTCG = unrealizedGains
        .filter((g) => g.type === "STCG")
        .reduce((sum, g) => sum + g.gain, 0);
    const totalUnrealizedLTCG = unrealizedGains
        .filter((g) => g.type === "LTCG")
        .reduce((sum, g) => sum + g.gain, 0);

    return (
        <>
            <ClientNavbar />
            <div className="page-content" style={{ background: "#f1f5f9" }}>
                    {/* Header */}
                    <div className="card shadow-sm p-4 mb-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <h3 className="text-primary mb-2">Capital Gains Report</h3>
                                <nav
                                    aria-label="breadcrumb"
                                    style={{ "--bs-breadcrumb-divider": "none" }}
                                >
                                    <ol className="breadcrumb mb-0">
                                        <li className="breadcrumb-item">
                                            <NavLink to="/overview">Dashboard</NavLink> /
                                        </li>
                                        <li className="breadcrumb-item">
                                            <NavLink to="/reports/capital-gains">Reports</NavLink> /
                                        </li>
                                        <li className="breadcrumb-item active">Capital Gains</li>
                                    </ol>
                                </nav>
                            </div>
                            <div className="d-flex gap-2 align-items-center flex-wrap mt-2">
                                {isExportReady && (
                                    <div className="d-flex align-items-center text-success me-2">
                                        <FiCheckCircle className="me-1" />
                                        <small>Export Ready</small>
                                    </div>
                                )}
                                <button
                                    className="btn btn-primary"
                                    onClick={handleExport}
                                    disabled={!isExportReady}
                                >
                                    <FiDownload className="me-1" />
                                    Export
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Date Range Filter */}
                    <div className="card shadow-sm p-4 mb-4">
                        <h5 className="text-primary mb-3">Date Range</h5>
                        <div className="row">
                            <div className="col-md-4 mb-3">
                                <label className="form-label fw-semibold">
                                    {/* <FiCalendar className="me-1" /> */}
                                    Start Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label fw-semibold">
                                    {/* <FiCalendar className="me-1" /> */}
                                    End Date
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>
                            <div className="col-md-4 mb-3 d-flex align-items-end">
                                <button
                                    className="btn btn-primary"
                                    onClick={handleGenerate}
                                    disabled={isGenerating}
                                >
                                    {isGenerating ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            ></span>
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            {/* <FiRefreshCw className="me-1" /> */}
                                            Generate Report
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="alert alert-info mt-3 mb-0">
                            <small>
                                <strong>Note:</strong> Large date ranges may take time to process
                                (async). Calculations follow FIFO (First In First Out) rules for
                                accurate capital gains computation.
                            </small>
                        </div>
                    </div>

                    {/* Summary Cards */}
                    <div className="row mb-4">
                        <div className="col-md-3 col-sm-6 mb-3">
                            <div className="card shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="text-muted mb-2">Total Realized Gains</h6>
                                    <h4 className="text-success mb-0">
                                        {formatINR(summaryData.totalRealizedGains)}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 col-sm-6 mb-3">
                            <div className="card shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="text-muted mb-2">Total Unrealized Gains</h6>
                                    <h4 className="text-primary mb-0">
                                        {formatINR(summaryData.totalUnrealizedGains)}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 col-sm-6 mb-3">
                            <div className="card shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="text-muted mb-2">STCG</h6>
                                    <h4 className="text-warning mb-0">
                                        {formatINR(summaryData.stcg)}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 col-sm-6 mb-3">
                            <div className="card shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="text-muted mb-2">LTCG</h6>
                                    <h4 className="text-info mb-0">
                                        {formatINR(summaryData.ltcg)}
                                    </h4>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Gains Chart */}
                    <div className="card shadow-sm p-4 mb-4">
                        <h5 className="text-primary mb-3">Gains by Type</h5>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={gainsChartData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis />
                                <Tooltip
                                    formatter={(value) => formatINR(value)}
                                />
                                <Legend />
                                <Bar dataKey="realized" fill="#1bafb4" name="Realized Gains" />
                                <Bar dataKey="unrealized" fill="#0f766e" name="Unrealized Gains" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* STCG/LTCG Summary */}
                    <div className="card shadow-sm p-4 mb-4">
                        <h5 className="text-primary mb-3">STCG/LTCG Summary</h5>
                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th>Type</th>
                                        <th className="text-end">Realized Gains</th>
                                        <th className="text-end">Unrealized Gains</th>
                                        <th className="text-end">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            <span className="badge bg-warning">STCG</span>
                                        </td>
                                        <td className="text-end">{formatINR(totalRealizedSTCG)}</td>
                                        <td className="text-end">{formatINR(totalUnrealizedSTCG)}</td>
                                        <td className="text-end fw-semibold">
                                            {formatINR(totalRealizedSTCG + totalUnrealizedSTCG)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <span className="badge bg-info">LTCG</span>
                                        </td>
                                        <td className="text-end">{formatINR(totalRealizedLTCG)}</td>
                                        <td className="text-end">{formatINR(totalUnrealizedLTCG)}</td>
                                        <td className="text-end fw-semibold">
                                            {formatINR(totalRealizedLTCG + totalUnrealizedLTCG)}
                                        </td>
                                    </tr>
                                </tbody>
                                <tfoot className="table-light">
                                    <tr>
                                        <th>Total</th>
                                        <th className="text-end">
                                            {formatINR(totalRealizedSTCG + totalRealizedLTCG)}
                                        </th>
                                        <th className="text-end">
                                            {formatINR(totalUnrealizedSTCG + totalUnrealizedLTCG)}
                                        </th>
                                        <th className="text-end">
                                            {formatINR(
                                                totalRealizedSTCG +
                                                totalRealizedLTCG +
                                                totalUnrealizedSTCG +
                                                totalUnrealizedLTCG
                                            )}
                                        </th>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    {/* Realized Gains Table */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Realized Gains</h5>
                            <span className="badge bg-success">
                                {realizedGains.length} Transactions
                            </span>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Fund Name</th>
                                            <th>Folio No</th>
                                            <th>Purchase Date</th>
                                            <th>Sale Date</th>
                                            <th>Holding Period</th>
                                            <th >Units</th>
                                            <th >Purchase NAV</th>
                                            <th >Sale NAV</th>
                                            <th >Purchase Amount</th>
                                            <th >Sale Amount</th>
                                            <th >Gain/Loss</th>
                                            <th>Type</th>
                                            <th>Tax Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {realizedGains.map((gain) => (
                                            <tr key={gain.id}>
                                                <td>
                                                    <div>
                                                        <strong>{gain.fundName}</strong>
                                                    </div>
                                                </td>
                                                <td>{gain.folioNo}</td>
                                                <td>{formatDate(gain.purchaseDate)}</td>
                                                <td>{formatDate(gain.saleDate)}</td>
                                                <td>{gain.holdingPeriod}</td>
                                                <td>{gain.units.toFixed(2)}</td>
                                                <td>{formatINR(gain.purchaseNAV)}</td>
                                                <td>{formatINR(gain.saleNAV)}</td>
                                                <td>
                                                    {formatINR(gain.purchaseAmount)}
                                                </td>
                                                <td>
                                                    {formatINR(gain.saleAmount)}
                                                </td>
                                                <td>
                                                    <span className="text-success">
                                                        {formatINR(gain.gain)} (
                                                        {formatPercent(gain.gainPercent)})
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`badge ${gain.type === "STCG" ? "bg-warning" : "bg-info"
                                                            }`}
                                                    >
                                                        {gain.type}
                                                    </span>
                                                </td>
                                                <td className="text-danger">
                                                    {formatINR(gain.taxAmount)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="table-light">
                                        <tr>
                                            <th colSpan="10" className="text-end">
                                                Total
                                            </th>
                                            <th >
                                                {formatINR(
                                                    realizedGains.reduce((sum, g) => sum + g.gain, 0)
                                                )}
                                            </th>
                                            <th></th>
                                            <th >
                                                {formatINR(
                                                    realizedGains.reduce((sum, g) => sum + g.taxAmount, 0)
                                                )}
                                            </th>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Unrealized Gains Table */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Unrealized Gains</h5>
                            <span className="badge bg-primary">
                                {unrealizedGains.length} Holdings
                            </span>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Fund Name</th>
                                            <th>Folio No</th>
                                            <th>Purchase Date</th>
                                            <th>Current Date</th>
                                            <th>Holding Period</th>
                                            <th>Units</th>
                                            <th>Purchase NAV</th>
                                            <th>Current NAV</th>
                                            <th>Purchase Amount</th>
                                            <th>Current Value</th>
                                            <th>Gain/Loss</th>
                                            <th>Type</th>
                                            <th>Estimated Tax</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {unrealizedGains.map((gain) => (
                                            <tr key={gain.id}>
                                                <td>
                                                    <div>
                                                        <strong>{gain.fundName}</strong>
                                                    </div>
                                                </td>
                                                <td>{gain.folioNo}</td>
                                                <td>{formatDate(gain.purchaseDate)}</td>
                                                <td>{formatDate(gain.currentDate)}</td>
                                                <td>{gain.holdingPeriod}</td>
                                                <td>{gain.units.toFixed(2)}</td>
                                                <td>{formatINR(gain.purchaseNAV)}</td>
                                                <td>{formatINR(gain.currentNAV)}</td>
                                                <td>
                                                    {formatINR(gain.purchaseAmount)}
                                                </td>
                                                <td>
                                                    {formatINR(gain.currentValue)}
                                                </td>
                                                <td>
                                                    <span className="text-success">
                                                        {formatINR(gain.gain)} (
                                                        {formatPercent(gain.gainPercent)})
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`badge ${gain.type === "STCG" ? "bg-warning" : "bg-info"
                                                            }`}
                                                    >
                                                        {gain.type}
                                                    </span>
                                                </td>
                                                <td className="text-danger">
                                                    {formatINR(gain.estimatedTax)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="table-light">
                                        <tr>
                                            <th colSpan="10" className="text-end">
                                                Total
                                            </th>
                                            <th>
                                                {formatINR(
                                                    unrealizedGains.reduce((sum, g) => sum + g.gain, 0)
                                                )}
                                            </th>
                                            <th></th>
                                            <th>
                                                {formatINR(
                                                    unrealizedGains.reduce(
                                                        (sum, g) => sum + g.estimatedTax,
                                                        0
                                                    )
                                                )}
                                            </th>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
           
        </>
    );
}
